import React, { useState } from 'react';
import { DeleteOutline } from '@mui/icons-material';
import { Colors } from 'library';
import { Alert, Modal, ModalFooter, PillButton, Stack, Text } from 'library/ui';
import { IBusinessLocation } from 'models';
import { consoleLogInDev } from 'shared';
import { useUserContext } from 'UserContext';
import { DeleteBlocker, deleteBusinessLocation, getBusinessLocationDeleteBlockers } from './businessLocationProfileApi';

export default function DeleteBusinessLocationButton({
    businessLocation,
    onDeleted,
}: {
    businessLocation: IBusinessLocation;
    onDeleted: () => void;
}) {
    const { refreshAlreadyLoggedInUser } = useUserContext();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [blockers, setBlockers] = useState<DeleteBlocker[]>([]);
    const [error, setError] = useState<string | null>(null);

    function openModal() {
        setOpen(true);
        setError(null);
        setLoading(true);
        getBusinessLocationDeleteBlockers(businessLocation.id)
            .then(setBlockers)
            .catch((e) => {
                consoleLogInDev(e);
                setError('Could not check whether this location can be deleted. Please try again.');
            })
            .finally(() => setLoading(false));
    }

    function confirmDelete() {
        setSubmitting(true);
        setError(null);
        deleteBusinessLocation(businessLocation.id)
            .then(() => {
                setOpen(false);
                onDeleted();
                refreshAlreadyLoggedInUser();
            })
            .catch((e) => {
                consoleLogInDev(e);
                if (e?.blockers?.length) {
                    setBlockers(e.blockers);
                } else {
                    setError(e?.detail ?? 'Could not delete this location. Please try again.');
                }
            })
            .finally(() => setSubmitting(false));
    }

    const blocked = blockers.length > 0;

    return (
        <>
            <PillButton variant="secondary" onClick={openModal} icon={<DeleteOutline sx={{ fontSize: 16 }} />}>
                Delete
            </PillButton>
            {open && (
                <Modal
                    title={`Delete ${businessLocation.name}?`}
                    onClose={() => !submitting && setOpen(false)}
                    width={480}
                >
                    <Stack gap="md">
                        {loading ? (
                            <Text tone="secondary">Checking for anything that still needs your attention...</Text>
                        ) : blocked ? (
                            <Alert variant="warning">
                                <Stack gap="sm">
                                    <Text weight="semibold">Resolve the following before deleting this location:</Text>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {blockers.map((b) => (
                                            <li key={b.code}>
                                                <Text>{b.label}</Text>
                                            </li>
                                        ))}
                                    </ul>
                                </Stack>
                            </Alert>
                        ) : (
                            <Text>
                                This location will be removed from your account and any active jobs or reminders for it
                                will be stopped. Past shifts, invoices, and timesheets are kept for your records.
                            </Text>
                        )}
                        {error && <Alert variant="error">{error}</Alert>}
                        <ModalFooter>
                            <PillButton variant="secondary" onClick={() => setOpen(false)} disabled={submitting}>
                                {blocked ? 'Close' : 'Cancel'}
                            </PillButton>
                            {!blocked && (
                                <PillButton
                                    onClick={confirmDelete}
                                    disabled={loading || submitting}
                                    color={Colors.error}
                                >
                                    {submitting ? 'Deleting...' : 'Delete location'}
                                </PillButton>
                            )}
                        </ModalFooter>
                    </Stack>
                </Modal>
            )}
        </>
    );
}
